import {
  Button,
  Divider,
  EButtonColorVariant,
  ETextStyleVariant,
  EToastVariant,
  LinkButton,
  Text,
} from '@outdoorsyco/bonfire';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  StripeCardElement,
  StripeCardElementChangeEvent,
  StripeCardElementOptions,
} from '@stripe/stripe-js';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import tailwind from 'tailwind';

import { breakpoint } from '@/constants/breakpoint';
import { useToast } from '@/hooks/useToast';
import useWindowSize from '@/hooks/useWindowSize';
import { addUserCards } from '@/redux/modules/creditCards';
import { createSetupIntent } from '@/services/stripe';
import { IFullUserInfo } from '@/services/types/user/user';
import { HSLStringToHEX } from '@/utility/colors';
import { useThunkDispatch } from '@/utility/redux/dispatch';

type TAddPaymentMethodProps = {
  user: IFullUserInfo;
  refreshUserCards: () => void;
};

export const AddPaymentMethod = ({ user, refreshUserCards }: TAddPaymentMethodProps) => {
  const intl = useIntl();
  const { showToast } = useToast();

  const windowSize = useWindowSize();
  const dispatch = useThunkDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const [showForm, setShowForm] = useState(false);
  const [isEmptyForm, setIsEmptyForm] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const paymentIntentsEnabled = user.has_setup_intent_enabled ? true : false;
  const country = user.bank_country || user.profile.address.country || 'US';

  const addAPaymentMethodLabel = intl.formatMessage({
    defaultMessage: 'Add a payment method',
    id: 'AiQP5M',
  });

  const getFontSize = () => {
    const remFont =
      windowSize?.width && windowSize?.width < breakpoint.md
        ? tailwind.theme.extend.fontSize['300']
        : tailwind.theme.extend.fontSize['500'];

    return `${parseFloat(remFont.replace('rem', '')) * 16}px`;
  };

  const options: StripeCardElementOptions = {
    hidePostalCode: true,
    style: {
      base: {
        color: HSLStringToHEX(tailwind.theme.colors.gray['900']),
        fontFamily: tailwind.theme.fontFamily.text,
        fontSize: getFontSize(),
        '::placeholder': {
          color: HSLStringToHEX(tailwind.theme.colors.gray['500']),
        },
      },
      invalid: {
        color: HSLStringToHEX(tailwind.theme.colors.red['800']),
      },
    },
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    setIsLoading(true);
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      setIsLoading(false);
      return;
    }

    try {
      const paymentTokenResponse = await stripe
        .createToken(elements.getElement(CardElement) as StripeCardElement)
        .then(response => response.token);

      if (!paymentTokenResponse?.card) {
        setIsLoading(false);

        showToast(
          EToastVariant.Error,
          intl.formatMessage({
            defaultMessage: 'Error',
            id: 'KN7zKn',
          }),
          intl.formatMessage({
            defaultMessage: 'Failed to generate Stripe token.',
            id: 'cSNaJS',
          }),
        );
        return;
      }

      // Not sure if this is the correct one (default is set when used) (works the same in ember)
      if (paymentIntentsEnabled) {
        const setupIntentResponse = await createSetupIntent({
          booking_id: 0,
          card_id: 0,
          card_owner_id: user.id,
          card_token: paymentTokenResponse?.id,
        });

        if (setupIntentResponse?.needs_confirmed) {
          const { error } = await stripe.confirmCardSetup(setupIntentResponse?.client_secret, {
            payment_method: setupIntentResponse?.stripe_card_id,
          });

          if (error) {
            await refreshUserCards();
            setIsLoading(false);

            showToast(
              EToastVariant.Error,
              intl.formatMessage({
                defaultMessage: 'Error',
                id: 'KN7zKn',
              }),
              intl.formatMessage({
                defaultMessage: 'Failed to confirm payment method.',
                id: 'yG2Ard',
              }),
            );
            return;
          }
        }
      }

      await dispatch(
        addUserCards(user.id, {
          brand: paymentTokenResponse.card.brand,
          default: false,
          exp_month: paymentTokenResponse.card.exp_month,
          exp_year: paymentTokenResponse.card.exp_year,
          last_four: paymentTokenResponse.card.last4,
          owner_id: user.id,
          stripe_token: paymentTokenResponse?.id,
          payment_processor: { country },
        }),
      );

      await refreshUserCards();

      setIsLoading(false);
      setShowForm(false);

      showToast(
        EToastVariant.Success,
        intl.formatMessage({
          defaultMessage: 'Success',
          id: 'xrKHS6',
        }),
        intl.formatMessage({
          defaultMessage: 'The payment method has been added.',
          id: '+U4Fvn',
        }),
      );
    } catch {
      await refreshUserCards();

      setIsLoading(false);

      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        intl.formatMessage({
          defaultMessage: 'Failed to add payment method.',
          id: 'QazXK7',
        }),
      );
    }
  };

  const handleChange = (value: StripeCardElementChangeEvent) => {
    setIsEmptyForm(value.empty);
    setHasError(value.error ? true : false);
    setIsComplete(value.complete);
  };

  const cardElementStyles = `rounded-soft border border-solid pl-2 py-3 rounded-lg ${
    isEmptyForm ? 'border-[#c8ceda]' : 'border-[#212831]'
  }`;

  return (
    <form onSubmit={handleSubmit}>
      {!showForm ? (
        <Button
          label={addAPaymentMethodLabel}
          aria-label={addAPaymentMethodLabel}
          onClick={() => setShowForm(true)}
          variant={EButtonColorVariant.Secondary}
          fullWidth={false}
        />
      ) : (
        <>
          <Text variant={ETextStyleVariant.LargeBold} className="mb-3">
            <FormattedMessage defaultMessage="Add new payment method" id="VXn6O3" />
          </Text>
          <div className={cardElementStyles}>
            <CardElement options={options} onChange={handleChange} />
          </div>
          <Divider className="mt-5 mb-3" />
          <div className="flex justify-between">
            <LinkButton
              label={intl.formatMessage({ defaultMessage: 'Cancel', id: '47FYwb' })}
              onClick={() => setShowForm(false)}
            />
            <Button
              label={intl.formatMessage({ defaultMessage: 'Save', id: 'jvo0vs' })}
              variant={EButtonColorVariant.Primary}
              type="submit"
              disabled={hasError || !isComplete}
              loading={isLoading}
            />
          </div>
        </>
      )}
    </form>
  );
};
